import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Link } from "wouter";
import {
  CalendarCheck,
  FileSignature,
  FileText,
  Gavel,
  Link2,
  Loader2,
  Paperclip,
  Scale,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { AvocatShell, type AvocatNavCounts } from "@/components/AvocatShell";
import { AvocatCorrespondancePanel } from "@/components/AvocatCorrespondance";
import { FormationGate } from "@/components/FormationGate";
import { useRole } from "@/contexts/AuthContext";
import {
  createAvocatConclusion,
  createClientPortalLink,
  getAvocatDashboard,
  listAvocatAudiences,
  listAvocatConclusions,
  listAvocatCorrespondance,
  listAvocatDecisions,
  listAvocatPieces,
  listClientPortalLinks,
  signAudienceConsentement,
  soumettreConclusion,
  televerserPiece,
  type AvocatAudience,
  type AvocatConclusion,
  type AvocatCorrespondance,
  type AvocatDashboard,
  type AvocatDecision,
  type AvocatPiece,
  type ClientPortalLink,
} from "@/lib/avocatApi";
import { getDossier } from "@/lib/apiProcedure";
import { CalendrierAvocat } from "@/components/procedure/CalendrierAvocat";
import { GelDepotGuard } from "@/components/procedure/ClotureDebatsButton";
import { RedfernSchedule } from "@/components/procedure/RedfernSchedule";
import { IncidentsPanel } from "@/components/procedure/IncidentsPanel";
import type { PhaseProcedurale } from "@/types/procedure";

const MARINE = "oklch(0.115 0.038 243)";
const GRAPHITE = "oklch(0.18 0.02 243)";
const GOLD = "oklch(0.695 0.105 68)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";

// Catégories de pièces (alignées sur le flux Nouveau dossier / backend).
const PIECE_CATEGORIES: { value: string; label: string }[] = [
  { value: "piece_jointe", label: "Pièce jointe (bordereau)" },
  { value: "requete", label: "Requête d'arbitrage" },
  { value: "convention_arbitrage", label: "Convention d'arbitrage" },
  { value: "preuve_envoi_partie_adverse", label: "Preuve d'envoi à la partie adverse" },
  { value: "preuve_paiement_frais", label: "Preuve de paiement des frais" },
  { value: "autre", label: "Autre" },
];

interface Data {
  dashboard: AvocatDashboard;
  decisions: AvocatDecision[];
  conclusions: AvocatConclusion[];
  pieces: AvocatPiece[];
  audiences: AvocatAudience[];
  portalLinks: ClientPortalLink[];
  correspondance: AvocatCorrespondance[];
}

type State =
  | { kind: "loading" }
  | { kind: "error"; detail: string }
  | { kind: "ready"; data: Data };

function Centered({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center"
      style={{ background: MARINE, color: "oklch(1 0 0)" }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="text-sm uppercase tracking-widest" style={{ color: GOLD }}>
      {children}
    </h2>
  );
}

function Row({ children }: { children: ReactNode }) {
  return (
    <li
      className="flex items-center gap-3 rounded-md border p-3"
      style={{ borderColor: BORDER }}
    >
      {children}
    </li>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="text-sm" style={{ opacity: 0.7 }}>
      {children}
    </p>
  );
}

const inputStyle: CSSProperties = {
  background: GRAPHITE,
  color: "oklch(1 0 0)",
  borderColor: BORDER,
};

function Badge({ children }: { children: ReactNode }) {
  return (
    <span
      className="ml-auto text-[10px] font-bold uppercase tracking-wider"
      style={{ color: GOLD }}
    >
      {children}
    </span>
  );
}

export default function DashboardAvocat() {
  const role = useRole();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [selDossier, setSelDossier] = useState<number | "">("");
  const [pieceFile, setPieceFile] = useState<File | null>(null);
  const [pieceCategorie, setPieceCategorie] = useState("piece_jointe");
  const [pieceBordereau, setPieceBordereau] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [
      dashboard,
      decisions,
      conclusions,
      pieces,
      audiences,
      portalLinks,
      correspondance,
    ] = await Promise.all([
      getAvocatDashboard(),
      listAvocatDecisions(),
      listAvocatConclusions(),
      listAvocatPieces(),
      listAvocatAudiences(),
      listClientPortalLinks(),
      listAvocatCorrespondance(),
    ]);
    setState({
      kind: "ready",
      data: {
        dashboard,
        decisions,
        conclusions,
        pieces,
        audiences,
        portalLinks,
        correspondance,
      },
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await load();
      } catch {
        if (!cancelled) {
          setState({
            kind: "error",
            detail:
              "Impossible de charger l'espace avocat. Votre compte doit être validé (Art. 52 AI Act) et rattaché à au moins un dossier.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  function resolveDossier(): number | null {
    if (typeof selDossier === "number") return selDossier;
    toast.error("Choisissez d'abord un dossier.");
    return null;
  }

  async function run(label: string, fn: () => Promise<unknown>) {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      await load();
    } catch {
      toast.error("Échec : " + label.toLowerCase() + ".");
    } finally {
      setBusy(false);
    }
  }

  const onCreateConclusion = () => {
    const d = resolveDossier();
    if (d == null) return;
    void run("Brouillon de conclusion créé", () => createAvocatConclusion(d));
  };

  const onDepositPiece = () => {
    const d = resolveDossier();
    if (d == null) return;
    if (!pieceFile) {
      toast.error("Sélectionnez un fichier à déposer.");
      return;
    }
    void run("Pièce déposée", async () => {
      await televerserPiece({
        dossier_id: d,
        categorie: pieceCategorie,
        file: pieceFile,
        bordereau_number: pieceBordereau.trim() || undefined,
        obligatoire: pieceCategorie === "preuve_envoi_partie_adverse",
      });
      setPieceFile(null);
      setPieceBordereau("");
    });
  };

  const onSubmitConclusion = (conclusionId: string) => {
    void run("Conclusion déposée", () => soumettreConclusion(conclusionId));
  };

  const onGeneratePortal = () => {
    const d = resolveDossier();
    if (d == null) return;
    if (!/.+@.+\..+/.test(clientEmail)) {
      toast.error("Adresse e-mail client invalide.");
      return;
    }
    void run("Lien client généré", async () => {
      // Lien permanent (pas d'expiration courte) : la sécurité repose sur l'OTP.
      await createClientPortalLink({
        dossier_id: d,
        client_email: clientEmail.trim(),
      });
      setClientEmail("");
    });
  };

  const onSignConsent = () => {
    const d = resolveDossier();
    if (d == null) return;
    void run("Consentement e-audience signé", () =>
      signAudienceConsentement({
        dossier_id: d,
        consentement_tsa: true,
        regles_visio: true,
      }),
    );
  };

  if (role && role !== "PARTIE" && role !== "ADMIN") {
    return (
      <Centered>
        <Scale size={36} style={{ color: GOLD }} />
        <p className="text-sm" style={{ opacity: 0.85 }}>
          Cet espace est réservé aux avocats (partie).
        </p>
        <Link href="/dashboard" className="text-sm underline" style={{ color: GOLD }}>
          ← Retour au tableau de bord
        </Link>
      </Centered>
    );
  }

  const counts: AvocatNavCounts | undefined =
    state.kind === "ready"
      ? {
          conclusions: state.data.conclusions.filter((c) => c.submitted_at != null)
            .length,
          pieces: state.data.pieces.length,
          audiences: state.data.audiences.length,
          decisions: state.data.decisions.length,
          client: state.data.portalLinks.length,
          correspondance: state.data.correspondance.length,
        }
      : undefined;

  return (
    <AvocatShell counts={counts}>
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Link
          href="/avocat/recherche"
          className="rounded-md border px-4 py-2 text-sm font-medium"
          style={{ borderColor: BORDER, color: GOLD }}
        >
          Recherche juridique
        </Link>
        <Link
          href="/avocat/nouveau-dossier"
          className="rounded-md px-4 py-2 text-sm font-medium text-black"
          style={{ background: GOLD }}
        >
          + Nouveau dossier d'arbitrage
        </Link>
      </div>

      {state.kind === "loading" && (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin" size={28} style={{ color: GOLD }} />
        </div>
      )}

      {state.kind === "error" && (
        <div
          className="rounded-md border p-5 text-sm"
          style={{
            borderColor: BORDER,
            background: "color-mix(in oklch, oklch(0.695 0.105 68) 6%, transparent)",
          }}
        >
          {state.detail}
        </div>
      )}

      {state.kind === "ready" && (
        <ReadyView
          data={state.data}
          selDossier={selDossier}
          setSelDossier={setSelDossier}
          pieceFile={pieceFile}
          setPieceFile={setPieceFile}
          pieceCategorie={pieceCategorie}
          setPieceCategorie={setPieceCategorie}
          pieceBordereau={pieceBordereau}
          setPieceBordereau={setPieceBordereau}
          clientEmail={clientEmail}
          setClientEmail={setClientEmail}
          busy={busy}
          onReload={() => void load()}
          onCreateConclusion={onCreateConclusion}
          onSubmitConclusion={onSubmitConclusion}
          onDepositPiece={onDepositPiece}
          onGeneratePortal={onGeneratePortal}
          onSignConsent={onSignConsent}
        />
      )}
    </AvocatShell>
  );
}

function ReadyView(props: {
  data: Data;
  selDossier: number | "";
  setSelDossier: (v: number | "") => void;
  pieceFile: File | null;
  setPieceFile: (v: File | null) => void;
  pieceCategorie: string;
  setPieceCategorie: (v: string) => void;
  pieceBordereau: string;
  setPieceBordereau: (v: string) => void;
  clientEmail: string;
  setClientEmail: (v: string) => void;
  busy: boolean;
  onReload: () => void;
  onCreateConclusion: () => void;
  onSubmitConclusion: (id: string) => void;
  onDepositPiece: () => void;
  onGeneratePortal: () => void;
  onSignConsent: () => void;
}) {
  const { data, busy } = props;
  const role = useRole();
  const hasDossiers = data.dashboard.dossiers.length > 0;

  // Contexte procédural du dossier ciblé : phase (gel des dépôts) + camp de
  // l'avocat (DEMANDEUR/DEFENDEUR) pour le calendrier contradictoire (L3/L4).
  const [phase, setPhase] = useState<PhaseProcedurale>(null);
  // Référence canonique (GSF-AAAA-XXXX) — doit figurer sur tous les actes.
  const [reference, setReference] = useState<string | null>(null);
  const selectedLien = data.dashboard.dossiers.find(
    (d) => d.dossier_id === props.selDossier,
  );
  const userSide =
    selectedLien?.partie === "DEFENDEUR" ? "DEFENDEUR" : "DEMANDEUR";

  useEffect(() => {
    let actif = true;
    if (props.selDossier === "") {
      setPhase(null);
      setReference(null);
      return;
    }
    getDossier(props.selDossier)
      .then((res) => {
        if (!actif) return;
        setPhase(res.data.phase_procedurale);
        setReference(res.data.reference ?? null);
      })
      .catch(() => {
        if (!actif) return;
        setPhase(null);
        setReference(null);
      });
    return () => {
      actif = false;
    };
  }, [props.selDossier]);

  return (
    <>
      {/* --- Actions conseil --- */}
      <section
        id="actions"
        className="rounded-md border p-4 space-y-4"
        style={{ borderColor: BORDER, background: "color-mix(in oklch, " + GOLD + " 5%, transparent)" }}
      >
        <SectionTitle>Actions conseil</SectionTitle>
        {!hasDossiers ? (
          <Empty>Aucun dossier rattaché : aucune action disponible.</Empty>
        ) : (
          <div className="space-y-3">
            <label className="block text-xs" style={{ color: GOLD }}>
              Dossier ciblé
              <select
                className="mt-1 block w-full rounded-md border px-2 py-1.5 text-sm"
                style={inputStyle}
                value={props.selDossier === "" ? "" : String(props.selDossier)}
                onChange={(e) =>
                  props.setSelDossier(e.target.value === "" ? "" : Number(e.target.value))
                }
              >
                <option value="">— Choisir un dossier —</option>
                {data.dashboard.dossiers.map((d) => (
                  <option key={d.id} value={String(d.dossier_id)}>
                    Dossier #{d.dossier_id} ({d.partie})
                  </option>
                ))}
              </select>
            </label>

            {props.selDossier !== "" && (
              <>
                {/* Référence canonique visible en clair sur les actes du dossier
                    + bouton de fermeture (déselection du dossier ciblé). */}
                <div
                  className="flex items-center justify-between gap-3 rounded-md border px-3 py-2"
                  style={{ borderColor: BORDER }}
                >
                  <div className="text-sm">
                    <span className="opacity-70">Dossier&nbsp;</span>
                    <span className="font-mono font-semibold" style={{ color: GOLD }}>
                      {reference ?? `#${props.selDossier}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => props.setSelDossier("")}
                    className="inline-flex items-center gap-1 text-xs opacity-80 hover:opacity-100"
                  >
                    <X size={14} /> Fermer
                  </button>
                </div>
                <GelDepotGuard phase={phase} />
                <CalendrierAvocat caseId={props.selDossier} userSide={userSide} />
                <RedfernSchedule caseId={props.selDossier} userSide={userSide} />
                {/* Élévation d'incident (récusation / exception d'incompétence,
                    L8) : la partie soulève ; le tribunal tranchera. La suggestion
                    CCJA reste masquée côté conseil (réservée à l'arbitre). */}
                <IncidentsPanel caseId={props.selDossier} canRaise role={role} />
              </>
            )}

            <div className="flex flex-wrap gap-2">
              <ActionButton disabled={busy} onClick={props.onCreateConclusion}>
                <FileSignature size={13} /> Nouveau brouillon de conclusion
              </ActionButton>
              <ActionButton disabled={busy} onClick={props.onSignConsent}>
                <CalendarCheck size={13} /> Signer le consentement e-audience
              </ActionButton>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2 rounded-md border p-3" style={{ borderColor: BORDER }}>
                <div className="text-xs" style={{ color: GOLD }}>Déposer une pièce</div>
                <select
                  className="block w-full rounded-md border px-2 py-1.5 text-sm"
                  style={inputStyle}
                  value={props.pieceCategorie}
                  onChange={(e) => props.setPieceCategorie(e.target.value)}
                >
                  {PIECE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
                <input
                  type="file"
                  className="block w-full text-xs"
                  onChange={(e) => props.setPieceFile(e.target.files?.[0] ?? null)}
                />
                <input
                  className="block w-full rounded-md border px-2 py-1.5 text-sm"
                  style={inputStyle}
                  placeholder="N° bordereau (optionnel)"
                  value={props.pieceBordereau}
                  onChange={(e) => props.setPieceBordereau(e.target.value)}
                />
                <ActionButton disabled={busy || !props.pieceFile} onClick={props.onDepositPiece}>
                  <Paperclip size={13} /> Déposer
                </ActionButton>
              </div>

              <div className="space-y-2 rounded-md border p-3" style={{ borderColor: BORDER }}>
                <div className="text-xs" style={{ color: GOLD }}>Lien portail client</div>
                <input
                  className="block w-full rounded-md border px-2 py-1.5 text-sm"
                  style={inputStyle}
                  placeholder="E-mail du client"
                  value={props.clientEmail}
                  onChange={(e) => props.setClientEmail(e.target.value)}
                />
                <ActionButton disabled={busy} onClick={props.onGeneratePortal}>
                  <Link2 size={13} /> Générer le lien
                </ActionButton>
              </div>
            </div>
            {busy && (
              <div className="flex items-center gap-2 text-xs" style={{ opacity: 0.7 }}>
                <Loader2 className="animate-spin" size={12} /> Traitement…
              </div>
            )}
          </div>
        )}
      </section>

      {/* --- Mes dossiers (Vue d'ensemble) --- */}
      <section id="overview" className="space-y-3 scroll-mt-20">
        <SectionTitle>Mes dossiers ({data.dashboard.total_dossiers})</SectionTitle>
        {data.dashboard.dossiers.length === 0 ? (
          <Empty>Aucun dossier rattaché pour le moment.</Empty>
        ) : (
          <ul className="space-y-2">
            {data.dashboard.dossiers.map((d) => (
              <li key={d.id}>
                {/* Étanchéité des rôles : la PARTIE reste dans son cockpit.
                    Cliquer cible le dossier dans « Actions conseil » plutôt que
                    de router vers la vue Arbitre (/cases/:id → 403). */}
                <button
                  type="button"
                  onClick={() => {
                    props.setSelDossier(d.dossier_id);
                    window.location.hash = "#actions";
                  }}
                  className="flex w-full items-center gap-3 rounded-md border p-3 text-left"
                  style={{ borderColor: BORDER }}
                >
                  <FileText size={16} style={{ color: GOLD }} />
                  <span className="font-mono text-sm">Dossier #{d.dossier_id}</span>
                  <Badge>{d.partie}</Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* --- Conclusions --- */}
      <section id="conclusions" className="space-y-3 scroll-mt-20">
        {/* Seules les conclusions DÉPOSÉES (submitted_at) sont décomptées : un
            brouillon n'est pas une écriture versée (intégrité du contradictoire). */}
        <SectionTitle>
          Mes conclusions ({data.conclusions.filter((c) => c.submitted_at != null).length} déposée
          {data.conclusions.filter((c) => c.submitted_at != null).length > 1 ? "s" : ""})
        </SectionTitle>
        <FormationGate feature="conclusions_companion">
          {data.conclusions.length === 0 ? (
            <Empty>Aucune session de conclusions.</Empty>
          ) : (
            <ul className="space-y-2">
              {data.conclusions.map((c) => (
                <Row key={c.id}>
                  <FileSignature size={16} style={{ color: GOLD }} />
                  <span className="text-sm">Dossier #{c.dossier_id}</span>
                  {c.submitted_at != null ? (
                    <Badge>déposée</Badge>
                  ) : (
                    <>
                      <Badge>brouillon</Badge>
                      <button
                        type="button"
                        disabled={props.busy}
                        onClick={() => props.onSubmitConclusion(c.id)}
                        className="rounded-md px-2.5 py-1 text-xs font-medium disabled:opacity-50"
                        style={{ background: GOLD, color: "oklch(0.18 0.02 243)" }}
                      >
                        Déposer
                      </button>
                    </>
                  )}
                </Row>
              ))}
            </ul>
          )}
        </FormationGate>
      </section>

      {/* --- Pieces --- */}
      <section id="pieces" className="space-y-3 scroll-mt-20">
        <SectionTitle>Mes pièces ({data.pieces.length})</SectionTitle>
        <FormationGate feature="depot_pieces">
          {data.pieces.length === 0 ? (
            <Empty>Aucune pièce versée.</Empty>
          ) : (
            <ul className="space-y-2">
              {data.pieces.map((p) => (
                <Row key={p.id}>
                  <Paperclip size={16} style={{ color: GOLD }} />
                  <span className="text-sm truncate">{p.filename}</span>
                  <span className="text-xs" style={{ opacity: 0.6 }}>#{p.dossier_id}</span>
                  <Badge>{p.statut}</Badge>
                </Row>
              ))}
            </ul>
          )}
        </FormationGate>
      </section>

      {/* --- Audiences --- */}
      <section id="audiences" className="space-y-3 scroll-mt-20">
        <SectionTitle>Audiences ({data.audiences.length})</SectionTitle>
        {data.audiences.length === 0 ? (
          <Empty>Aucun consentement e-audience enregistré.</Empty>
        ) : (
          <ul className="space-y-2">
            {data.audiences.map((a) => (
              <Row key={a.id}>
                <CalendarCheck size={16} style={{ color: GOLD }} />
                <span className="text-sm">Dossier #{a.dossier_id}</span>
                <Badge>{a.signed_at ? "Signé" : "En attente"}</Badge>
              </Row>
            ))}
          </ul>
        )}
      </section>

      {/* --- Liens portail client --- */}
      <section id="client" className="space-y-3 scroll-mt-20">
        <SectionTitle>Liens client ({data.portalLinks.length})</SectionTitle>
        {data.portalLinks.length === 0 ? (
          <Empty>Aucun lien portail généré.</Empty>
        ) : (
          <ul className="space-y-2">
            {data.portalLinks.map((l) => (
              <Row key={l.id}>
                <Link2 size={16} style={{ color: GOLD }} />
                <span className="text-sm truncate">{l.client_email}</span>
                <span className="text-xs" style={{ opacity: 0.6 }}>#{l.dossier_id}</span>
                <button
                  type="button"
                  onClick={() => {
                    const url = `${window.location.origin}/portail/${l.token}`;
                    void navigator.clipboard?.writeText(url);
                    toast.success("Lien du portail copié.");
                  }}
                  className="ml-auto rounded-md border px-2.5 py-1 text-xs"
                  style={{ borderColor: BORDER, color: GOLD }}
                >
                  Copier le lien
                </button>
              </Row>
            ))}
          </ul>
        )}
      </section>

      {/* --- Correspondance protocolaire --- */}
      <section id="correspondance" className="space-y-3 scroll-mt-20">
        <SectionTitle>Correspondance ({data.correspondance.length})</SectionTitle>
        <FormationGate feature="correspondance_protocolaire">
          <AvocatCorrespondancePanel
            dossiers={data.dashboard.dossiers}
            messages={data.correspondance}
            currentAvocatId={data.dashboard.avocat_id}
            onSent={props.onReload}
          />
        </FormationGate>
      </section>

      {/* --- Decisions notifiees (lecture seule) --- */}
      <section id="decisions" className="space-y-3 scroll-mt-20">
        <SectionTitle>Décisions notifiées ({data.decisions.length})</SectionTitle>
        <FormationGate feature="analyse_post_sentence">
          {data.decisions.length === 0 ? (
            <Empty>Aucune décision notifiée (lecture seule).</Empty>
          ) : (
            <ul className="space-y-2">
              {data.decisions.map((dec) => (
                <Row key={dec.id}>
                  <Gavel size={16} style={{ color: GOLD }} />
                  <span className="text-sm">
                    {dec.decision_type ?? "Décision"} — dossier #{dec.dossier_id}
                  </span>
                  {dec.delai_recours != null && (
                    <span className="ml-auto text-xs" style={{ opacity: 0.7 }}>
                      Recours : {dec.delai_recours} j
                    </span>
                  )}
                </Row>
              ))}
            </ul>
          )}
        </FormationGate>
      </section>
    </>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50"
      style={{ background: GOLD, color: "oklch(0.18 0.02 243)" }}
    >
      {children}
    </button>
  );
}
